import { Logger } from '../support/Logger';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { DevSettings } from '../support/DevSettings';

export class IndexedDBConnectionError extends Error {
    constructor(message: string = 'IndexedDB connection failed after multiple retries') {
        super(message);
        this.name = 'IndexedDBConnectionError';
    }
}

const MAX_RECONNECT_ATTEMPTS = 3;
const INITIAL_RETRY_DELAY_MS = 500;

export class IndexedDBFactory<T extends { id: IDBValidKey }> {
    static inject = () => [Logger, MixPanelEventEmitter, DevSettings];
    constructor(private logger: Logger, private tracker: MixPanelEventEmitter, private devSettings: DevSettings) {}

    // either creates or returns an existing DB
    get = (dbName: string, storeName: string, version: number = 1) => {
        return new IndexedDBManager<T>(dbName, storeName, version, this.logger, this.tracker, this.devSettings);
    };
};

export class IndexedDBManager<T extends { id: IDBValidKey }> {
    private dbPromise: Promise<IDBDatabase>;
    private reconnectAttempts = 0;

    constructor(
        private dbName: string,
        private storeName: string,
        private version: number = 1,
        private logger: Logger,
        private tracker: MixPanelEventEmitter,
        private devSettings: DevSettings
    ) {
        this.dbName = dbName;
        this.storeName = storeName;
        this.version = version;
        this.logger = logger;
        this.dbPromise = this.initializeDB();
    };

    private initializeDB = async (): Promise<IDBDatabase> => {
        // Check dev flag to simulate failure for testing
        if (this.devSettings.simulateIndexedDBFailure) {
            return Promise.reject(new Error('IndexedDB simulated failure (dev flag enabled)'));
        }

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(this.dbName, this.version);

            request.onerror = (event) => {
                reject(`IndexedDB error: ${(event.target as IDBRequest).error}`);
            };

            request.onsuccess = (event) => {
                resolve((event.target as IDBRequest).result);
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBRequest).result as IDBDatabase;
                if (!db.objectStoreNames.contains(this.storeName)) {
                    db.createObjectStore(this.storeName, { keyPath: 'id' });
                }
            };
        });
    };

    private reInitializeDB = async (postInitialization?: () => Promise<any>, operation?: 'add' | 'update' | 'get' | 'delete' | 'getAll'): Promise<void> => {
        if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            this.logger.error(`IndexedDB max reconnection attempts (${MAX_RECONNECT_ATTEMPTS}) exceeded`);
            this.tracker.track('IndexedDB Connection Failed', () => ({
                operation,
                attemptsExhausted: MAX_RECONNECT_ATTEMPTS
            }));
            throw new IndexedDBConnectionError();
        }

        this.reconnectAttempts++;
        const currentAttempt = this.reconnectAttempts; // Capture for logging (counter may change during nested calls)

        // Exponential backoff: 500ms, 1000ms, 2000ms
        const delay = INITIAL_RETRY_DELAY_MS * Math.pow(2, currentAttempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));

        this.logger.info(`IndexedDB reconnection attempt ${currentAttempt}/${MAX_RECONNECT_ATTEMPTS}`);

        // Try to close the old database connection, but don't fail if we can't
        // (the connection may already be broken, which is why we're reinitializing)
        try {
            const db = await this.dbPromise;
            db.close();
        } catch (error) {
            // Old connection may already be broken - that's fine, continue with reinitialization
        }

        // Create new database connection
        this.dbPromise = this.initializeDB();

        if (postInitialization) {
            try {
                await postInitialization();
                // Track successful reconnection with attempt number
                if (operation) {
                    this.tracker.track('IndexedDB Reconnection Success', () => ({
                        operation,
                        attemptNumber: this.reconnectAttempts,
                        totalAttempts: this.reconnectAttempts
                    }));
                }
                // Reset counter on success
                this.reconnectAttempts = 0;
            } catch (error) {
                this.logger.error(`Post-initialization ${operation} operation failed (attempt ${currentAttempt}):`, error);
                // Recursively retry if more attempts available
                if (this.reconnectAttempts < MAX_RECONNECT_ATTEMPTS) {
                    await this.reInitializeDB(postInitialization, operation);
                } else {
                    throw error;
                }
            }
        }
    };

    async getCapacityEstimate() {
        const estimate = await navigator.storage.estimate();
        console.log(estimate);
        return estimate;
    };

    private getTransaction = async () => {
        let tx: IDBTransaction;
        const db = await this.dbPromise;

        try {
            tx = db.transaction(this.storeName, 'readwrite');
        } catch(err: any) {
            if (err.name === 'InvalidStateError') {
                await this.initializeDB();
                tx = db.transaction(this.storeName, 'readwrite');
            } else {
                throw err;
            }
        }

        return tx;
    };

    private promisifyRequest = <R>(request: IDBRequest): Promise<R> => {
        return new Promise((resolve, reject) => {
            request.onsuccess = (event) => resolve((event.target as IDBRequest).result as R);
            request.onerror = (event) => reject((event.target as IDBRequest).error);
        });
    };

    private attemptRetry = async <R>(params: {
        operation: 'add' | 'update' | 'get' | 'delete' | 'getAll';
        retryOperation: () => Promise<R>;
    }): Promise<R> => {
        const { operation, retryOperation } = params;
        let result!: R;
        await this.reInitializeDB(async () => {
            result = await retryOperation();
        }, operation);
        return result;
    };

    private executeRequest = async <R>(params: {
        operation: 'add' | 'update' | 'get' | 'delete' | 'getAll';
        getRequest: () => Promise<IDBRequest>;
        retryOperation: () => Promise<R>;
    }): Promise<R> => {
        const { operation, getRequest, retryOperation } = params;

        try {
            const request = await getRequest();
            return await this.promisifyRequest<R>(request);
        } catch (error: any) {
            const message = error?.message || error?.toString() || 'Unknown error';
            this.logger.error(`IndexedDB error during ${operation}: ${message}`);
            return await this.attemptRetry({ operation, retryOperation });
        }
    };

    async add(data: T): Promise<IDBValidKey> {
        return this.executeRequest<IDBValidKey>({
            operation: 'add',
            getRequest: async () => {
                const tx = await this.getTransaction();
                const store = tx.objectStore(this.storeName);
                return store.add(data);
            },
            retryOperation: () => this.add(data)
        });
    };

    async get(id: IDBValidKey): Promise<T | undefined> {
        return this.executeRequest<T | undefined>({
            operation: 'get',
            getRequest: async () => {
                const tx = await this.getTransaction();
                const store = tx.objectStore(this.storeName);
                return store.get(id);
            },
            retryOperation: () => this.get(id)
        });
    };

    async update(data: T): Promise<void> {
        try {
            const tx = await this.getTransaction();
            const store = tx.objectStore(this.storeName);

            const existingData = await this.promisifyRequest<T>(store.get(data.id));
            if (!existingData) {
                throw new Error(`Record with key ${data.id} not found`);
            }

            const mergedData = { ...existingData, ...data };
            await this.promisifyRequest<IDBValidKey>(store.put(mergedData));
        } catch (error: any) {
            // Don't retry if record not found - that's a logical error, not a connection error
            if (error?.message?.includes('not found')) {
                throw error;
            }

            const message = error?.message || error?.toString() || 'Unknown error';
            this.logger.error(`IndexedDB error during update: ${message}`);
            return await this.attemptRetry({
                operation: 'update',
                retryOperation: () => this.update(data)
            });
        }
    };

    async delete(id: IDBValidKey): Promise<void> {
        const result = await this.executeRequest<void>({
            operation: 'delete',
            getRequest: async () => {
                const tx = await this.getTransaction();
                const store = tx.objectStore(this.storeName);
                return store.delete(id);
            },
            retryOperation: () => this.delete(id)
        });
        console.log(`deleted recording with id ${id}`);
        return result;
    };

    async getAll(): Promise<T[]> {
        return this.executeRequest<T[]>({
            operation: 'getAll',
            getRequest: async () => {
                const tx = await this.getTransaction();
                const store = tx.objectStore(this.storeName);
                return store.getAll();
            },
            retryOperation: () => this.getAll()
        });
    };
};
